/* eslint-disable @typescript-eslint/no-unused-vars */
import { mapHudItemToDip } from "components/Hud/helper";
import { IShopPurchaseOffer } from "contexts/WebStoreProvider/types";
import { HudDipItem } from "interfaces";

type DataType = Record<
	string,
	Omit<
		IShopPurchaseOffer,
		"onlyNonPayers" | "priority" | "purchaseButtonBannerText" | "bannerText"
	>[]
>;
type OfferType = Omit<
	IShopPurchaseOffer,
	"onlyNonPayers" | "priority" | "purchaseButtonBannerText" | "bannerText"
>;

type StickyGroup = {
	campaignGroup: string;
	offers: OfferType[];
	isHudEnriched: boolean;
};

export const enrichStickyDataWithHud = (
	stickyData: DataType,
	hudItems: HudDipItem[] = []
): DataType => {
	const groups = Object.entries(stickyData).map<StickyGroup>(
		([campaignGroup, offers]) => {
			if (offers.length === 0 || offers[0].dip) {
				return { campaignGroup, offers, isHudEnriched: false };
			}

			const hudItem = hudItems.find(({ offerIds = [] }) =>
				offers.every(({ id }) => offerIds.includes(id))
			);
			const hudDip = hudItem ? mapHudItemToDip(hudItem) : undefined;

			if (!hudDip?.dip) {
				return { campaignGroup, offers, isHudEnriched: false };
			}

			return {
				campaignGroup,
				isHudEnriched: true,
				offers: offers.map((offer) => ({
					...offer,
					dip: hudDip.dip,
					timerExpirationDate:
						offer.timerExpirationDate ?? hudItem?.dipConfig?.timerExpirationDate
				}))
			};
		}
	);

	const displayableGroups = groups.filter(
		({ offers }) =>
			!!offers[0]?.dip &&
			!offers.some(({ dip }) => dip?.triggerType === "bouncyChestClick")
	);

	const uniqueGroups = displayableGroups.reduce<StickyGroup[]>((acc, group) => {
		const groupId = group.offers[0]?.dip?.id ?? group.campaignGroup;
		const duplicateIndex = acc.findIndex(
			(existingGroup) =>
				(existingGroup.offers[0]?.dip?.id ?? existingGroup.campaignGroup) ===
				groupId
		);

		if (duplicateIndex === -1) {
			acc.push(group);
		} else if (acc[duplicateIndex].isHudEnriched && !group.isHudEnriched) {
			acc[duplicateIndex] = group;
		}

		return acc;
	}, []);

	return uniqueGroups.reduce<DataType>((acc, { campaignGroup, offers }) => {
		acc[campaignGroup] = offers;
		return acc;
	}, {});
};

export const SortStyckyDips = (stickyData: DataType, contentLength: number) => {
	const availableSlots = 5 - contentLength;
	const sortedStickyDataByDates = Object.entries(stickyData)
		.sort(
			(
				[keyA, arrayA]: [string, OfferType[]],
				[keyB, arrayB]: [string, OfferType[]]
			) => {
				//console.log(keyA, keyB);
				if (!arrayA[0] || !arrayB[0]) {
					return 0;
				}
				const dateA = arrayA[0].timerExpirationDate
					? new Date(arrayA[0].timerExpirationDate).getTime()
					: null;
				const dateB = arrayB[0].timerExpirationDate
					? new Date(arrayB[0].timerExpirationDate).getTime()
					: null;

				if (dateA === null && dateB === null) {
					return 0;
				}
				if (dateA === null) {
					return 1;
				}
				if (dateB === null) {
					return -1;
				}

				return dateA - dateB;
			}
		)
		.slice(0, availableSlots);
	return sortedStickyDataByDates;
};
